import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CloudUpload, ArrowBack } from '@mui/icons-material';
import { fetchEmployees, createEmployeeDocument } from '../../../services/employee';

const DOCUMENT_TYPES = [
  { value: 'contract', labelKey: 'employees:documents.types.contract' },
  { value: 'certificate', labelKey: 'employees:documents.types.certificate' },
  { value: 'id_copy', labelKey: 'employees:documents.types.idCopy' },
  { value: 'resume', labelKey: 'employees:documents.types.resume' },
  { value: 'passport', labelKey: 'employees:documents.types.passport' },
  { value: 'other', labelKey: 'employees:documents.types.other' },
];

const ACCEPT_FILE_TYPES = [
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.doc',
  '.docx',
  '.pdf',
  '.xls',
  '.xlsx',
  '.csv',
  '.txt',
].join(',');


const Upload = () => {
  const { t } = useTranslation(['common', 'employees']);
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    employee: '',
    document_type: '',
    title: '',
    file: null,
    expiry_date: '',
    notes: '',
  });

  useEffect(() => {
    const load = async () => {
      setLoadingEmployees(true);
      try {
        const res = await fetchEmployees({ page_size: 500 });
        const list = res.results || (Array.isArray(res) ? res : []);
        setEmployees(list);
      } catch (e) {
        setError(e.response?.data?.detail || e.message || 'Failed to load employees');
      } finally {
        setLoadingEmployees(false);
      }
    };
    load();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setForm((prev) => ({ ...prev, file }));
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.employee || !form.document_type || !form.title?.trim() || !form.file) {
      setError('Please select an employee, document type, enter a title, and choose a file.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        employee: form.employee,
        document_type: form.document_type,
        title: form.title.trim(),
        file: form.file,
      };
      if (form.expiry_date) payload.expiry_date = form.expiry_date;
      if (form.notes?.trim()) payload.notes = form.notes.trim();
      await createEmployeeDocument(payload);
      setSuccess(true);
      setForm({ employee: '', document_type: '', title: '', file: null, expiry_date: '', notes: '' });
      setTimeout(() => {
        navigate('/admin/employees/documents');
      }, 1500);
    } catch (err) {
      const msg = err.response?.data?.detail
        || (typeof err.response?.data === 'object' && Object.values(err.response?.data).flat().join(' '))
        || err.message
        || 'Upload failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        px: { xs: 0.75, sm: 1, md: 1.5 },
        py: { xs: 2, sm: 2.5, md: 3 },
        maxWidth: 720,
        mx: 'auto',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin/employees/documents')}
          sx={{ color: '#DC143C' }}
        >
          {t('common:back')}
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          {t('employees:documents.uploadDocument')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Document uploaded successfully. Redirecting to list...
        </Alert>
      )}

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControl fullWidth required>
                <InputLabel>{t('employees:documents.employee')}</InputLabel>
                <Select
                  value={form.employee}
                  onChange={(e) => handleChange('employee', e.target.value)}
                  label={t('employees:documents.employee')}
                  disabled={loadingEmployees}
                >
                  <MenuItem value="">
                    <em>Select employee</em>
                  </MenuItem>
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.full_name || emp.employee_id || `Employee #${emp.id}`}
                      {emp.employee_id ? ` (${emp.employee_id})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth required>
                <InputLabel>{t('employees:documents.documentType')}</InputLabel>
                <Select
                  value={form.document_type}
                  onChange={(e) => handleChange('document_type', e.target.value)}
                  label={t('employees:documents.documentType')}
                >
                  <MenuItem value="">
                    <em>Select type</em>
                  </MenuItem>
                  {DOCUMENT_TYPES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Title"
                value={form.title}
                onChange={(e) => handleChange('title', e.target.value)}
                required
                fullWidth
                placeholder="e.g. Employment Contract 2024"
              />

              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  File (allowed: JPG, PNG, PDF, DOC, DOCX, XLS, XLSX, CSV, TXT)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUpload />}
                  fullWidth
                  sx={{ py: 2, borderStyle: 'dashed' }}
                >
                  {form.file ? form.file.name : 'Choose file'}
                  <input
                    type="file"
                    hidden
                    accept={ACCEPT_FILE_TYPES}
                    onChange={handleFileChange}
                  />
                </Button>
              </Box>

              <TextField
                label="Expiry date (optional)"
                type="date"
                value={form.expiry_date}
                onChange={(e) => handleChange('expiry_date', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Notes (optional)"
                value={form.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                fullWidth
                multiline
                rows={2}
              />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 1 }}>
                <Button
                  type="button"
                  onClick={() => navigate('/admin/employees/documents')}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting || !form.employee || !form.document_type || !form.title?.trim() || !form.file}
                  sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
                >
                  {submitting ? <CircularProgress size={24} /> : 'Upload'}
                </Button>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Upload;
